import { compact, filter, map, uniqBy } from 'es-toolkit/compat';

import { ExperimentResult } from 'interfaces/experimentResults';
import { AttributesTable, slideResultToAttributesTable } from 'interfaces/inferenceArtifacts';
import { FeatureMetadata, heatmapUrlToPresignedGetUrl, heatmapUrlToPresignedHeadUrl } from './featureMetadata';
import { getPresentationInfoForOptionKey } from './parseHeatmaps';

export interface ParquetArtifactEntry {
  id: string;
  orchestrationId?: string;
  title: string;
  urlForGetRequest: string;
  urlForHeadRequest?: string;
}

export const getParquetArtifactEntries = (
  heatmaps: FeatureMetadata[],
  fallbackTitle: string,
  attributesTable?: AttributesTable
): ParquetArtifactEntry[] =>
  uniqBy(
    compact(
      map(
        filter(
          heatmaps,
          (heatmap) =>
            !attributesTable ||
            (Boolean(heatmap.resultType) && slideResultToAttributesTable[heatmap.resultType] === attributesTable)
        ),
        (heatmap) => {
          if (!heatmap.heatmapUrl) {
            return undefined;
          }
          const urlForGetRequest = heatmapUrlToPresignedGetUrl(heatmap.heatmapUrl);

          return urlForGetRequest
            ? {
                id: heatmap.orchestrationId || heatmap.id,
                orchestrationId: heatmap.orchestrationId,
                title: fallbackTitle,
                urlForGetRequest,
                urlForHeadRequest: heatmapUrlToPresignedHeadUrl(heatmap.heatmapUrl),
              }
            : undefined;
        }
      )
    ),
    'urlForGetRequest'
  );

export const getParquetArtifactEntriesFromExperimentResult = (
  experimentResult: ExperimentResult,
  fallbackTitle: string,
  attributesTable?: AttributesTable
): ParquetArtifactEntry[] =>
  getParquetArtifactEntries(
    compact(
      map(experimentResult.options, (option) => {
        const presentationInfo = getPresentationInfoForOptionKey(experimentResult, option.key);

        return presentationInfo && typeof presentationInfo !== 'string' && presentationInfo.file_type === 'parquet'
          ? ({
              id: `${experimentResult.experimentResultId}-${option.key}`,
              heatmapUrl: presentationInfo.storage_url,
              orchestrationId: experimentResult.orchestrationId,
              resultType: experimentResult.resultType,
            } as FeatureMetadata)
          : undefined;
      })
    ),
    fallbackTitle,
    attributesTable
  );
